/*LIBRARY*/
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { FileUploader } from "react-drag-drop-files";

/*COMPONENT*/
import Modal from "../common_modal/Modal";
import SHP_CSV from "./SHP_CSV";
import GeojsonDetails from "./GeojsonDetails";
import DataTypeConfirmation from "./DataTypeConfirmation";
import NaNEvidence from "./NaNEvidence";

/*REDUX*/
import { importLayer, deleteLayer } from "../../App/actions/layerNewActions";
import { post_activities } from "../../App/actions/activities";

/*PICTURE*/

/*FUNCTION*/
import { generate_geo_layer } from "../../App/validation/generateGeoJson";
import { is_lng_lat_valid } from "../../App/validation/geojson_validation";
import { handle_upload_geojson } from "../../App/validation/handle_file";

/*DATA*/
import dict from "../../Data/dict.json";
import { set_value_properties } from "../../App/actions/propertiesActions";

/*CONST*/

const UPLOAD_LAYER = ({
  project,
  layer,
  auth,
  properties,
  importLayer,
  deleteLayer,
  set_value_properties,
  post_activities,
}) => {
  const [folder, set_folder] = useState("");
  const [is_detail_open, set_is_detail_open] = useState(false);
  const [detail_index, set_detail_index] = useState("");
  const [warning, set_warning] = useState({
    is_open: false,
    content_name: "",
    message: "",
  });
  const [selected_color, set_selected_color] = useState("#1a8bc0");
  const [is_not_fly_to_batch_geojson, set_is_not_fly_to_batch_geojson] =
    useState(true);
  const [is_uploading, set_is_uploading] = useState(false);
  const [target_data_types, set_target_data_types] = useState([]);
  const [nan_evidence, set_nan_evidence] = useState({});

  const isMounted = useRef(true);

  const { files_geojson, modal_upload, mode_step, file } = properties;
  const { user, language: auth_language } = auth;
  const { geo_project, loadingDetail } = project;
  const { loadingProcess, itemLoading } = layer;

  const filesGeojsonRef = useRef(files_geojson);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    filesGeojsonRef.current = files_geojson;
  }, [files_geojson]);

  const handleChangeGeojsonFiles = async (files_object) => {
    let files = [];
    for (let key of Object.keys(files_object)) {
      const file_obj = files_object[key];
      const new_file = await handleValidationFiles(file_obj);
      if (new_file) {
        files.push(new_file);
      }
    }

    files.sort((a, b) =>
      a.fileName > b.fileName ? 1 : b.fileName > a.fileName ? -1 : 0,
    );

    const files_with_new_format = [...filesGeojsonRef.current, ...files];

    set_value_properties({
      key: "files_geojson",
      value: files_with_new_format,
    });
  };

  const handleValidationFiles = async (file_obj) => {
    const filename = file_obj.name.split(".")[0];
    const ext = file_obj.name.split(".").at(-1);
    const is_any = filesGeojsonRef.current.find(
      (element) => element.fileName === filename,
    );

    if (is_any === undefined && ext?.toLowerCase() === "geojson") {
      let new_file = await handle_upload_geojson(file_obj);

      // ADDED: explicitly track extension for batch files
      if (new_file) {
        new_file["ext"] = ext.toLowerCase();
      }

      if (
        new_file?.validation_result?.is_valid_in_general &&
        new_file?.validation_result?.invalid_number > 0 &&
        new_file?.validation_result?.valid_number > 0
      ) {
        new_file["status"] = "semi-valid";
      } else if (new_file?.validation_result?.is_valid_in_general) {
        new_file["status"] = "valid";
      } else {
        new_file["status"] = "invalid";
      }

      return new_file;
    } else {
      return undefined;
    }
  };

  const saveGeojsonFilesToDatabase = async () => {
    const geo_project_id = geo_project?._id;
    const filtered_files_geojson = filesGeojsonRef.current.filter(
      (f) =>
        f.validation_result?.is_valid_in_general &&
        (f.status === "valid" ||
          f.status === "failed" ||
          f.status === "semi-valid"),
    );
    const timer = (ms) => new Promise((res) => setTimeout(res, ms));

    set_is_uploading(true);

    for (let f of filtered_files_geojson) {
      setIsBatchGeojsonUploaded(f.fileName, "uploading");

      // ADDED: Pass f.ext to handleSaveGeojson
      const { isSuccess, error, layer_id } = await handleSaveGeojson(
        f.data,
        f.fileName,
        f.headers,
        f.ext,
      );

      await timer(5000);
      if (isSuccess) {
        setIsBatchGeojsonUploaded(f.fileName, "success");
      } else {
        let body = {
          geo_project_id: geo_project_id,
          geo_layer_id: layer_id,
        };
        await deleteLayer(body);
        setIsBatchGeojsonUploaded(f.fileName, "failed", error);
      }
    }
    if (isMounted.current) set_is_uploading(false);
  };

  const toggleIsFlyToBatchGeojson = () => {
    set_is_not_fly_to_batch_geojson(!is_not_fly_to_batch_geojson);
  };

  const removeGeojson = (filename) => {
    const new_files_geojson = filesGeojsonRef.current.filter(
      (f) => f.fileName !== filename,
    );
    set_value_properties({
      key: "files_geojson",
      value: new_files_geojson,
    });
  };

  // ADDED: Accept 'ext' parameter
  const handleSaveGeojson = async (file_data, fileName, headers_local, ext) => {
    const type = file_data?.features?.[0]?.geometry?.type || "Point";

    const user_id = user?._id;
    const geo_project_id = geo_project?._id;
    const is_any_target_data_types = target_data_types?.length > 0;

    const geojson = generate_geo_layer(
      {
        columns: is_any_target_data_types ? target_data_types : headers_local,
        is_use_data_type: is_any_target_data_types ? true : false,
        type,
      },
      {
        fileName,
        file: file_data?.features,
        colorStatus: selected_color,
        user_id: user_id,
        geo_project_id,
        folder,
      },
    );

    const response = await importLayer(geojson, is_not_fly_to_batch_geojson);

    // ADDED: Dynamically resolve action based on the extension
    let action_name = "upload_geojson";
    if (ext) {
      action_name = `upload_${ext}`;
    }

    await post_activities({
      action: action_name,
    });

    return response;
  };

  const set_file = (value) => {
    set_value_properties({
      key: "file",
      value: value,
    });
  };

  const validateTheData = async () => {
    const language = localStorage?.language ? localStorage?.language : "ina";
    const latitude = file?.latitude || "";
    const longitude = file?.longitude || "";

    if (!latitude || !longitude) {
      toggleModalWarning("no_lat_long_header", true);
    } else {
      let result;
      for (let row of file?.data || []) {
        let lat = Number(String(row[latitude]).replace(",", "."));
        let long = Number(String(row[longitude]).replace(",", "."));

        result = is_lng_lat_valid(long, lat);
        if (!result) {
          break;
        }
      }

      if (result) {
        await handleSaveFile();
        handleResetCsv();
        toggleUpload(false);
      } else {
        toggleModalWarning(
          "invalid_warning",
          true,
          `${dict["The accepted coordinate type is latitude longitude of type WGS84 / CGS in decimal degrees."][language]} ${dict["Decimal must be separated by dot. Ex: 106.8858778"][language]}`,
        );
      }
    }
  };

  const toggleModalWarning = (content_name, value, message) => {
    set_warning({
      is_open: value !== undefined ? value : !warning.is_open,
      content_name: content_name || "",
      message: message || "",
    });
  };

  const setLongitude = (event) => {
    set_value_properties({
      key: "file",
      value: {
        ...file,
        longitude: event.target.value,
      },
    });
  };

  const handleSaveFile = async () => {
    const user_id = user?._id;
    const geo_project_id = geo_project?._id;

    const geojson = generate_geo_layer(
      {
        columns: file?.headers,
      },
      {
        fileName: file?.fileName,
        file: file?.data,
        colorStatus: selected_color,
        user_id: user_id,
        geo_project_id,
        folder,
        latitude: file?.latitude,
        longitude: file?.longitude,
      },
    );
    toggleStep("");
    if (warning.is_open || modal_upload) {
      set_warning({
        is_open: false,
        content_name: "",
        message: "",
      });
      toggleUpload(false);
    }

    await importLayer(geojson, !is_not_fly_to_batch_geojson);

    // ADDED: Dynamically detect CSV vs XLSX for tracking
    let action_name = "upload_csv";
    if (file?.ext === "xlsx") {
      action_name = "upload_xlsx";
    }

    await post_activities({
      action: action_name,
    });
  };

  const setLatitude = (event) => {
    set_value_properties({
      key: "file",
      value: {
        ...file,
        latitude: event.target.value,
      },
    });
  };

  const handleResetCsv = () => {
    set_selected_color("#1a8bc0");

    set_value_properties({
      key: "files_geojson",
      value: [],
    });

    set_value_properties({
      key: "mode_step",
      value: "",
    });

    set_value_properties({
      key: "file",
      value: {},
    });
  };

  const handleClearGeojson = () => {
    set_value_properties({
      key: "files_geojson",
      value: [],
    });
  };

  const toggleStep = (value) =>
    set_value_properties({
      key: "mode_step",
      value,
    });

  const toggleUpload = (value) => {
    set_value_properties({
      key: "modal_upload",
      value: value ?? !modal_upload,
    });
  };

  const toggleModalDetail = (value, idx) => {
    set_is_detail_open(value !== undefined ? value : !is_detail_open);
    set_detail_index(idx);
  };

  const handleChange = (e) => {
    const target = e.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;
    if (name === "folder") {
      set_folder(value);
    }
  };

  const setIsBatchGeojsonUploaded = (filename, value, error) => {
    const current_files = [...filesGeojsonRef.current];
    const index = current_files.findIndex((f) => f.fileName === filename);
    if (index === -1) return;

    const file_to_update = { ...current_files[index] };
    file_to_update.status = value;

    if (value === "success") {
      delete file_to_update.file;
    }

    if (error) {
      file_to_update.validation_result = {
        ...file_to_update.validation_result,
        error,
        is_valid_in_general: false,
      };
    }

    current_files[index] = file_to_update;

    filesGeojsonRef.current = current_files;
    set_value_properties({
      key: "files_geojson",
      value: current_files,
    });
  };

  const handleSingleFile = async () => {
    // ADDED: Extract 'ext' from the file state
    const { data, fileName, headers: headers_local, ext } = file;

    const geo_project_id = geo_project?._id;

    // ADDED: Pass 'ext' to handleSaveGeojson
    const { isSuccess, error, layer_id } =
      (await handleSaveGeojson(data, fileName, headers_local, ext)) || {};

    if (!isSuccess) {
      let body = {
        geo_project_id: geo_project_id,
        geo_layer_id: layer_id,
      };
      await deleteLayer(body);
      toggleModalWarning("failed_to_Upload", true, error);
    }
    handleResetCsv();
    toggleUpload(false);
  };

  //local storage
  const language = localStorage?.language || auth_language;

  const is_all_success = files_geojson.every(
    (item) => item.status === "success",
  );
  const { headers } = file;

  const folders_array = geo_project?.folders || [];
  let folders_state = [];
  if (!loadingDetail && geo_project.folders) {
    folders_state = [...folders_array];
    folders_state.unshift({
      name: dict?.["Outside folders"]?.[language],
      _id: "",
    });
  }

  const button_upload = (
    <button
      onClick={() => toggleUpload()}
      className="button background_black"
      style={{ color: "white" }}
      data-mapid="clickUploadFile2"
    >
      {dict?.["Upload file"]?.[language]}
    </button>
  );

  let latlong_setup_ui = (
    <section style={{ display: "grid", gridTemplateColumns: "auto auto" }}>
      <div
        style={{
          marginRight: "20px",
        }}
      >
        <h2
          style={{
            fontSize: "20px",
          }}
        >
          Latitude:
        </h2>
        <span>
          <select onChange={setLatitude} data-mapid="selectLatitude">
            <option value="">Select</option>
            {headers?.map((header, idx) => (
              <option key={idx} value={header}>
                {header}
              </option>
            ))}
          </select>
        </span>
      </div>

      <div>
        <h2
          style={{
            fontSize: "20px",
          }}
        >
          Longitude:
        </h2>
        <span>
          <select onChange={setLongitude} data-mapid="selectLongitude">
            <option value="">Select</option>
            {headers?.map((header, idx) => (
              <option key={idx} value={header}>
                {header}
              </option>
            ))}
          </select>
        </span>
      </div>
    </section>
  );

  let modal_content = (
    <main>
      <section style={{ textAlign: "center" }} className="text_bold">
        {dict?.["Upload File"]?.[language]}
      </section>
      <div style={{ display: "block" }}>
        {dict?.["Folder layer"]?.[language]}
      </div>
      <select
        id="folder"
        name="folder"
        value={folder}
        className="input_new"
        onChange={handleChange}
      >
        {folders_state.map(({ _id, name }, idx) => {
          return (
            <option key={idx} value={_id}>
              {name}
            </option>
          );
        })}
      </select>
      <section>
        <SHP_CSV
          file={file}
          setFile={set_file}
          toggleStep={toggleStep}
          folder={folder}
          toggleUpload={toggleUpload}
        />
      </section>
    </main>
  );

  if (loadingProcess && itemLoading === "importLayer") {
    modal_content = (
      <section style={{ textAlign: "center" }} className="text_bold">
        {dict["Uploading"][language]}...
      </section>
    );
  }

  if (mode_step === "file-step-1") {
    const { data, headers: data_headers, latitude, longitude } = file;
    modal_content = (
      <div>
        {latlong_setup_ui}

        <section className="scroll_auto">
          <table className="table w_full">
            <thead>
              <tr>
                {data_headers?.map((header, idx) => (
                  <th key={idx} className="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.slice(0, 5).map((row, idx_row) => (
                <tr key={idx_row}>
                  {data_headers?.map((header, idx_col) => (
                    <td key={idx_col}>{row[header]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="marginTop_10 flex justify_between align_center">
          <button onClick={handleResetCsv} className="button background_black">
            {dict?.["Back"]?.[language]}
          </button>

          <button
            style={{
              backgroundColor: !latitude || !longitude ? "#777777" : "#0ca5eb",
            }}
            onClick={validateTheData}
            className="button background_black"
            data-mapid="clickNext"
          >
            {dict?.["Next"]?.[language]}
          </button>
        </section>
      </div>
    );
  } else if (mode_step === "geojson-step-1") {
    modal_content = (
      <div>
        <label className="font_30">{dict["Type data"][language]}</label>
        <section>{`total features = ${file?.data?.features?.length}`}</section>

        <section className="h_800 w_full">
          <DataTypeConfirmation
            file={file}
            set_target_data_types={set_target_data_types}
            target_data_types={target_data_types}
          />
        </section>

        <section className="marginTop_10 flex justify_between align_center">
          <button onClick={handleResetCsv} className="button background_black">
            {dict?.["Back"]?.[language]}
          </button>

          {file?.status && file.status !== "success" && (
            <button
              className="button background_black"
              onClick={() => {
                toggleStep("geojson-step-2");
                const myDiv = document.getElementById(
                  "modal_upload_not_a_number",
                );
                if (myDiv) myDiv.scrollTop = 0;
              }}
            >
              {dict["Next"][language]}
            </button>
          )}
        </section>
      </div>
    );
  } else if (mode_step === "geojson-step-2") {
    modal_content = (
      <div>
        <label className="font_20">NaN Feature</label>
        <section>
          {
            dict?.[
              "If you confirm, these NaN (Not a Number) values will be change to 0"
            ]?.[language]
          }
        </section>
        <section>
          <NaNEvidence
            nan_evidence={nan_evidence}
            set_nan_evidence={set_nan_evidence}
            target_data_types={target_data_types}
            file={file}
          />
        </section>
        <section className="margin_top flex justify_between align_center">
          <button
            onClick={() => toggleStep("geojson-step-1")}
            className="button background_black"
          >
            {dict?.["Back"]?.[language]}
          </button>

          <button
            className="button background_black"
            onClick={() => {
              handleSingleFile();
            }}
          >
            {dict?.["Confirm and start upload"]?.[language]}
          </button>
        </section>
      </div>
    );
  } else if (mode_step === "bulk-step-1") {
    modal_content = (
      <div>
        <section>
          <h1>{dict?.["Batch Upload"]?.[language]}</h1>
        </section>
        <section className="center_perfect">
          <FileUploader
            classes="container_upload absolute border_dash"
            multiple={true}
            handleChange={handleChangeGeojsonFiles}
            name="file"
            types={["GEOJSON"]}
            children={
              <div
                className="container_upload center_perfect"
                data-mapid="uploadBulk"
              >
                <div>{dict?.["Drop geojson files here"]?.[language]}</div>
              </div>
            }
          />
        </section>
        <section className="w_full flex justify_between margin_top">
          <span>
            <button
              className={`multi_check ${
                is_not_fly_to_batch_geojson === true
                  ? "bg_grey"
                  : "background_black"
              }`}
              onClick={() => {
                toggleIsFlyToBatchGeojson();
              }}
            />
            <label className="w_400">
              {
                dict?.["Fly to all of these layers after upload complete"]?.[
                  language
                ]
              }
            </label>
          </span>
          <div>
            <button
              onClick={() => {
                handleClearGeojson();
              }}
              className="button background_black"
              data-mapid="clickClear"
            >
              {dict["Clear All"][language]}
            </button>
          </div>
        </section>

        <section>
          <ul className="card_parent">
            {files_geojson?.map((file_item, idx) => {
              let color_status;
              if (file_item.status === "valid") {
                color_status = "#0ca5eb";
              } else if (file_item.status === "semi-valid") {
                color_status = "#f25a1d";
              } else if (file_item.status === "invalid") {
                color_status = "#d41616";
              } else if (file_item.status === "success") {
                color_status = "#44a889";
              } else if (file_item.status === "failed") {
                color_status = "#d41616";
              } else {
                color_status = "#616161";
              }

              return (
                <li
                  className="card_list bg_lightGrey grid grid_15 padding_2"
                  key={idx}
                >
                  <div className="font_14 center_perfect">{`${idx + 1}.`}</div>
                  <div className="text_left flex align_center span_10">
                    {file_item.fileName}
                  </div>
                  <div
                    className="span_4"
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                    }}
                  >
                    <div
                      className="button center_perfect background_black"
                      onClick={() => toggleModalDetail(true, idx)}
                    >
                      i
                    </div>
                    <div
                      className="container_card center_perfect"
                      style={{
                        background: color_status,
                        color: "#ffffff",
                      }}
                    >
                      {file_item.status}
                    </div>
                    <button
                      className="center_perfect"
                      style={{
                        background: !is_uploading ? "#d41616" : "#e4e4e4",
                        color: "#ffffff",
                        height: "35px",
                        width: "35px",
                        borderRadius: "5px",
                        fontSize: "20px",
                        cursor: !is_uploading ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (!is_uploading) {
                          removeGeojson(file_item.fileName);
                        }
                      }}
                      data-mapid={`clickDelete-${idx}`}
                    >
                      x
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
        <section className="margin_top flex justify_between align_center">
          <button
            onClick={() => {
              if (!is_uploading) {
                set_value_properties({
                  key: "mode_step",
                  value: "",
                });
              }
            }}
            style={{
              cursor: is_uploading ? "default" : "pointer",
              background: is_uploading ? "#c4c4c4" : "#0ca5eb",
            }}
            className="button background_black"
          >
            {dict?.["Back"]?.[language]}
          </button>

          <button
            onClick={() => {
              if (!is_uploading && !is_all_success) {
                saveGeojsonFilesToDatabase();
              } else if (is_all_success) {
                toggleUpload();
              }
            }}
            style={{
              cursor: is_uploading ? "default" : "pointer",
              background: is_uploading ? "#c4c4c4" : "#0ca5eb",
            }}
            className="button background_black"
            data-mapid="clickNext"
          >
            {is_all_success
              ? dict?.["Done"]?.[language]
              : dict?.["Next"]?.[language]}
          </button>
        </section>
      </div>
    );
  } else if (mode_step === "invalid-file-content") {
    const { error } = file;

    modal_content = (
      <div>
        <section>
          <h2>{dict["Invalid file content"][language]}</h2>
          {error && <h3>{error}</h3>}
        </section>
        <section className="marginTop_10 flex justify_between align_center">
          <button onClick={handleResetCsv} className="button background_black">
            {dict?.["Back"]?.[language]}
          </button>
        </section>
      </div>
    );
  }

  const modalJsonDetailContent = is_detail_open && (
    <Modal
      modalSize="small"
      id="modal"
      isOpen={is_detail_open}
      onClose={toggleModalDetail}
    >
      <main className="box-body">
        <GeojsonDetails geojson_list={files_geojson} index={detail_index} />
      </main>
    </Modal>
  );

  const warning_no_long_lat_header_content = (
    <div>
      <p style={{ width: "350px", textAlign: "center" }}>
        {dict?.["Warning latitude longitude"]?.[language]}
      </p>
      <div className="center_perfect">
        <button
          onClick={async () => {
            await handleSaveFile();
            toggleUpload(false);
          }}
          className="button background_black"
        >
          {dict?.["Next"]?.[language]}
        </button>
      </div>
    </div>
  );

  const invalid_warning_content = (
    <div>
      <p style={{ width: "350px", textAlign: "center" }}>{warning?.message}</p>
    </div>
  );

  const failed_to_upload_content = (
    <main>
      <header className="text_center bold font_20">Error</header>
      <section>
        <label className="bold font_14">
          {dict?.["Error Message"]?.[language]}:
        </label>
        <p className="padding_5 h_full border rounded_5">{warning?.message}</p>
      </section>
    </main>
  );

  const modalWarning = warning.is_open && (
    <Modal
      modalSize="small"
      id="modal"
      isOpen={warning.is_open}
      onClose={toggleModalWarning}
    >
      <main className="box-body">
        {warning.content_name === "no_lat_long_header" &&
          warning_no_long_lat_header_content}
        {warning.content_name === "invalid_warning" && invalid_warning_content}
        {warning.content_name === "failed_to_Upload" &&
          failed_to_upload_content}
      </main>
    </Modal>
  );

  const modal_upload_content = modal_upload && (
    <Modal
      modalSize="medium"
      id="modal"
      isOpen={modal_upload}
      onClose={() => toggleUpload(false)}
    >
      <main id="modal_upload_not_a_number" className="box-body">
        {modal_content}
      </main>
    </Modal>
  );

  return (
    <main className="white-link">
      {button_upload}
      {modal_upload_content}
      <br />
      {modalWarning}
      {modalJsonDetailContent}
    </main>
  );
};

const mapStateToProps = (state) => {
  return {
    project: state.project,
    layer: state.layer,
    auth: state.auth,
    properties: state.properties,
  };
};

export default connect(mapStateToProps, {
  importLayer,
  deleteLayer,
  set_value_properties,
  post_activities,
})(UPLOAD_LAYER);
